/**
 * #613 / #614 — GET /api/apps: the platform and the apps published on it, each with the menu bar
 * its module declaration (G55 module contract, L1 api/src/modules/**) fills, already filtered to
 * the caller's roles. The web never hardcodes an app, a menu or a route; it renders this.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface AppRef {
  id: string;
  key: string;
  name: string;
  slug?: string | null;
}

export interface MenuItemDecl {
  id: string;
  label: string;
  route: string;
  roles?: readonly string[];
  /** R-14 T-087 (L2, optional): the lowest experience tier that shows this item; the disclosure registry overrides it */
  minTier?: number;
  /** R-14 T-087 (L2): the disclosure element id the item is read under (= its id) */
  elementId?: string;
  /** R-16 T-135 (optional): the mega-menu column the item sits in (items with the same group share a titled column) */
  group?: string;
  /** R-16 T-135 (optional): one line under the label, in business words */
  hint?: string;
}

export type MenuKind = 'standard' | 'app';

export interface MenuDecl {
  /** standard menus: app · data · requests · help; an app's own menus carry their own ids */
  id: string;
  label: string;
  kind: MenuKind;
  items: MenuItemDecl[];
  /** Round 12 #728: an Admin group's rail section ("Work", "Model & data" …) and its one-line subtitle */
  section?: string;
  subtitle?: string;
}

export interface PublishedApp {
  module: string;
  slug: string;
  name: string;
  record: AppRef;
  runsOn: AppRef | null;
  /** ASSUMED (not in L1's relayed shape yet): the app's owner, shown on the portal card */
  owner?: { id: string; key?: string; name: string } | null;
  /** ASSUMED optional: one line about the app for its card */
  description?: string | null;
  menuBar: MenuDecl[];
  adminPages?: unknown[];
  /** R-14 T-068 (L2): the app's home per scope kind; `{scopeKey}` is filled from GET /api/me/home */
  homes?: { scopeKind: string; route: string }[];
  /** R-15 T-107 (L2): the app's landing page, and the bar entries (`app` or a menu id) whose label returns to it */
  homeRoute?: string;
  homeLinks?: string[];
  /** R-15 T-078 (L2): the entity types the app owns — its registry switcher lists these */
  entityTypes?: string[];
}

export interface AppsResponse {
  /** R11 #649 #653: the platform declaration's entries and Admin groups, filtered to the caller */
  /** R-16 T-135: `elements` — the shell's own elements (quick-links row, L0 ▾ menus) and their default tier */
  platform: { name: string | null; slug: string | null; entries?: MenuItemDecl[]; adminGroups?: MenuDecl[]; dataSections?: MenuItemDecl[]; elements?: { id: string; minTier: number; note?: string }[] };
  apps: PublishedApp[];
}

export const appsQueryKey = ['cigs', 'apps'] as const;

export function useApps(enabled = true) {
  return useQuery<AppsResponse>({
    queryKey: appsQueryKey,
    queryFn: () => apiFetch<AppsResponse>('/apps'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** R-14 T-068: what "Open <app>" links to — the home for the user's scope, resolved on arrival. */
export const appOpenPath = (app: Pick<PublishedApp, 'slug'>) => `/apps/${app.slug}/home`;

/** The fallback home: the app's first own menu's first item, else the first item anywhere. */
export function appHome(app: PublishedApp): string | null {
  const own = app.menuBar.find((m) => m.kind === 'app' && m.items.length > 0);
  return own?.items[0]?.route ?? app.menuBar.flatMap((m) => m.items)[0]?.route ?? null;
}

/** The app whose menu bar owns this path (longest matching item route wins); null on platform pages. */
export function appForPath(apps: readonly PublishedApp[], pathname: string): PublishedApp | null {
  let best: { app: PublishedApp; len: number } | null = null;
  for (const app of apps) {
    for (const item of app.menuBar.flatMap((m) => m.items)) {
      const route = item.route.split(/[?#]/)[0] ?? '';
      if (route.length > 1 && (pathname === route || pathname.startsWith(`${route}/`)) && route.length > (best?.len ?? 0)) {
        best = { app, len: route.length };
      }
    }
  }
  return best?.app ?? null;
}
